import { useEffect, useMemo } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import { MdTranslate } from 'react-icons/md';
import { useActiveLocales } from '@features/locales/hooks';
import type { Locale } from '@features/locales/schema';
import { useCropOptions } from '@features/crops/hooks';
import type { CropOption } from '@features/crops/schema';
import { ImageUploadField } from '@features/newsArticles/components/cells/ImageUploadField';
import {
  varietyFormSchema,
  type Variety,
  type VarietyDetail,
  type VarietyFormTranslation,
  type VarietyFormValues,
} from '../schema';
import { useCreateVariety, useUpdateVariety, useVariety } from '../hooks';

interface Props {
  open: boolean;
  variety: Variety | null;
  onClose: () => void;
}

function buildFormTranslations(
  locales: Locale[],
  existing: VarietyDetail['translations'] | undefined,
): VarietyFormTranslation[] {
  const byCode = new Map(existing?.map((t) => [t.localeCode, t]) ?? []);
  return locales
    .filter((l) => !l.isDefault)
    .map((locale) => ({
      localeCode: locale.code,
      localeName: locale.name,
      name: byCode.get(locale.code)?.name ?? '',
    }));
}

function buildEmptyDefaults(
  locales: Locale[],
  existing?: VarietyDetail | null,
): VarietyFormValues {
  return {
    cropId: existing?.cropId ?? '',
    code: existing?.code ?? '',
    name: existing?.name ?? '',
    imageStorageKey: existing?.imageStorageKey ?? null,
    thumbnailStorageKey: existing?.thumbnailStorageKey ?? null,
    isActive: existing?.isActive ?? true,
    translations: buildFormTranslations(locales, existing?.translations),
  };
}

export function VarietyFormDialog({ open, variety, onClose }: Props) {
  const isEditing = !!variety;
  const createMutation = useCreateVariety();
  const updateMutation = useUpdateVariety();
  const submitting = createMutation.isPending || updateMutation.isPending;

  const { data: locales = [], isLoading: localesLoading } = useActiveLocales();
  const { data: crops = [] } = useCropOptions();
  const detailQuery = useVariety(variety?.id ?? null);
  const detail = detailQuery.data ?? null;

  const dataReady = !localesLoading && (!isEditing || detailQuery.isSuccess);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<VarietyFormValues>({
    resolver: zodResolver(varietyFormSchema) as Resolver<VarietyFormValues>,
    defaultValues: useMemo(() => buildEmptyDefaults([]), []),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    if (!open || !dataReady) return;
    reset(buildEmptyDefaults(locales, isEditing ? detail : null));
  }, [open, dataReady, locales, detail, isEditing, reset]);

  const cropId = watch('cropId');
  const translations = watch('translations');

  const cropById = useMemo(
    () => new Map(crops.map((c) => [c.id, c])),
    [crops],
  );
  const cropValue: CropOption | null = cropId ? cropById.get(cropId) ?? null : null;

  const onSubmit = handleSubmit(async (values) => {
    if (isEditing && variety) {
      await updateMutation.mutateAsync({ id: variety.id, values });
    } else {
      await createMutation.mutateAsync(values);
    }
    onClose();
  });

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Edit variety' : 'New variety'}</DialogTitle>
      <DialogContent dividers>
        {!dataReady ? (
          <Box className="flex items-center justify-center py-12">
            <CircularProgress size={28} />
          </Box>
        ) : (
          <form id="variety-form" onSubmit={onSubmit} className="grid gap-4 pt-2">
            <Controller
              control={control}
              name="cropId"
              render={({ field }) => (
                <Autocomplete<CropOption>
                  size="small"
                  options={crops}
                  value={cropValue}
                  disabled={isEditing}
                  onChange={(_, opt) => field.onChange(opt ? opt.id : '')}
                  getOptionLabel={(opt) => opt.name}
                  isOptionEqualToValue={(opt, v) => opt.id === v.id}
                  renderInput={(p) => (
                    <TextField
                      {...p}
                      label="Crop"
                      error={!!errors.cropId}
                      helperText={
                        errors.cropId?.message ??
                        (isEditing
                          ? 'Crop is immutable post-create.'
                          : 'Pick the crop this variety belongs to.')
                      }
                    />
                  )}
                />
              )}
            />

            <TextField
              label="Code"
              size="small"
              fullWidth
              disabled={isEditing}
              error={!!errors.code}
              helperText={
                errors.code?.message ??
                (isEditing
                  ? 'Code is immutable post-create.'
                  : 'Lowercase alphanumerics joined by hyphens (e.g. long-staple).')
              }
              {...register('code')}
            />

            <TextField
              label="Name"
              size="small"
              fullWidth
              error={!!errors.name}
              helperText={errors.name?.message ?? 'Base (default-locale) display name.'}
              {...register('name')}
            />

            <Controller
              control={control}
              name="imageStorageKey"
              render={({ field }) => (
                <div>
                  <label className="mb-1.5 block text-[12.5px] font-medium text-foreground">
                    Image
                  </label>
                  <ImageUploadField
                    folder="varieties"
                    imageStorageKey={field.value}
                    thumbnailStorageKey={watch('thumbnailStorageKey')}
                    onChange={(next) => {
                      setValue('imageStorageKey', next.imageStorageKey, {
                        shouldValidate: false,
                        shouldDirty: true,
                      });
                      setValue(
                        'thumbnailStorageKey',
                        next.thumbnailStorageKey,
                        { shouldValidate: false, shouldDirty: true },
                      );
                    }}
                    error={errors.imageStorageKey?.message}
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="isActive"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={
                    <span className="text-[13px] text-foreground">
                      {field.value
                        ? 'Active — appears in pickers and consumer surfaces'
                        : 'Inactive — hidden from new attachments; existing references preserved'}
                    </span>
                  }
                />
              )}
            />

            {translations.length > 0 && (
              <div className="grid gap-3">
                <div className="-mb-1 mt-2 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-foreground-muted">
                  <MdTranslate size={14} />
                  Translations <span className="font-normal normal-case">· optional</span>
                </div>
                {translations.map((t, i) => {
                  const nameErr = errors.translations?.[i]?.name?.message;
                  return (
                    <TextField
                      key={t.localeCode}
                      label={`Name (${t.localeName})`}
                      size="small"
                      fullWidth
                      error={!!nameErr}
                      helperText={nameErr ?? ' '}
                      {...register(`translations.${i}.name` as const)}
                    />
                  );
                })}
              </div>
            )}
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="variety-form"
          variant="contained"
          disabled={submitting || !dataReady}
        >
          {submitting ? 'Saving…' : isEditing ? 'Save changes' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
